/**
 * API endpoints
 */

const config = {
  // API healthcheck
  healthcheck: "/healthcheck/internal",

  // AUTH
  jwt: "/auth/generateToken",

  // the current user plus default shard, if any.
  user: "/control/self", // aka /control/{id|self}

  // the current user's available shards
  shards: "/control/shards",

  // current shard user
  "shard-user": "/accounts/self",

  // used to persist a default shard
  defaultShard: "/control/defaultShard",

  // USERS AND ROLES
  // returns a list of users
  // /accounts / { id } returns a specific user by account id
  users: "/accounts/self",
  managers: "/accounts?role=ROLE_CHART_REVIEW_ADMIN",
  reviewers: "/accounts?role=ROLE_CHART_REVIEWER",

  // members
  "member:eligibility": "/members/{id}/eligibility",
  "member:claims": "/members/{id}/claim-numbers", // accepts: fromDateOfService & throughDateOfService
  "member:mapped-hccs": "/members/{id}/mapped-hccs", // accepts: diagCode & throughDateOfService
  // member HCC categories
  "member:current-categories": "/members/{id}/current-categories",
  "member:expected-categories": "/members/{id}/expected-categories", // used by Categories Meta section on DRG Edit screens
  "member:accepted-categories": "/members/{id}/accepted-categories",
  "member:missing-categories": "/members/{id}/missing-categories",
  // approve claim diag deletes - BugzId 85537
  "members:claim-diag-deletes:bulk-update:filtered":
    "/members/{id}/claim-diag-deletes-bulk-approval/create-filtered",
  "members:claim-diag-deletes:bulk-update:selected":
    "/members/{id}/claim-diag-deletes-bulk-approval/create-selected",
  "members:claim-diag-deletes:bulk-update:errors":
    "/members/claim-diag-deletes-bulk-approval/errors/{workSid}",

  // claims
  claim: "/claims/{id}",

  // CHART-REVIEWS
  "chart-reviews": "/chart-reviews",
  "chart-reviews:projects": "/chart-reviews/projects",
  "chart-reviews:projects:validate-name":
    "/chart-reviews/projects/validate-name",
  "chart-reviews:statuses": "/chart-reviews/chart-review-statuses",
  "chart-reviews:na-reason-codes": "/chart-reviews/na-reason-codes",
  "chart-reviews:claim-selection-types": "/chart-reviews/claim-selection-types",
  "chart-reviews:retrievalStatuses": "/chart-reviews/chart-retrieval-statuses",
  "chart-reviews:chartAttributes": "/chart-reviews/chart-attributes",
  "chart-reviews:places-of-service": "/chart-reviews/place-of-services",
  "chart-reviews:supplemental-reason-codes":
    "/chart-reviews/supplemental-reason-codes",
  "chart-reviews:drg-adds-action-types": "/chart-reviews/action-types",
  "chart-reviews:drg-deletes-action-types":
    "/chart-reviews/action-types-for-deletes",
  "chart-reviews:diagnosis-attributes":
    "/chart-reviews/diagnosis-review-groups-adds/diagnosis-attributes",
  "chart-reviews:notes": "/chart-reviews/{id}/notes",
  // Bulk Edit BugzId 83907
  "chart-reviews:bulk-update-selected":
    "/chart-reviews/charts/bulk-update-selected",
  "chart-reviews:bulk-update-filtered":
    "/chart-reviews/charts/bulk-update-filtered",
  // Bulk Assignments
  "chart-reviews:bulk-assign-selected":
    "/chart-reviews/chart-reviewers/bulk-update-selected",
  "chart-reviews:bulk-assign-filtered":
    "/chart-reviews/chart-reviewers/bulk-update-filtered",
  // bulk chart imports
  "chart-reviews:bulk-import": "/chart-reviews/bulk-import/chart-review-{type}",
  // Diagnosis Review Group endpoints
  "chart-reviews:drg-current":
    "/chart-reviews/{id}/diagnosis-review-groups-adds", // Same endpoint as drg-adds, used in Chart Review -> Current Diags tab
  "chart-reviews:drg-adds":
    "/chart-reviews/{chartId}/diagnosis-review-groups-adds",
  "chart-reviews:drg-deletes": "/chart-reviews/diagnosis-review-groups-deletes",
  "chart-reviews:drg-aux-types":
    "/chart-reviews/diagnosis-review-group-aux-types",
  "chart-reviews:drg-adds-notes":
    "/chart-reviews/{chartId}/diagnosis-review-groups-adds/{drgId}/notes",
  "chart-reviews:drg-deletes-notes":
    "/chart-reviews/diagnosis-review-groups-deletes/{id}/notes",
  "chart-reviews:drg-adds-audit-history":
    "/chart-reviews/{chartId}/diagnosis-review-groups-adds/{drgId}/audit-history",
  "chart-reviews:drg-deletes-audit-history":
    "/chart-reviews/diagnosis-review-groups-deletes/{id}/audit-history",
  "chart-reviews:drg-adds-file-history":
    "/chart-reviews/{chartId}/diagnosis-review-groups-adds/{drgId}/file-history",
  "chart-reviews:drg-deletes-file-history":
    "/chart-reviews/diagnosis-review-groups-deletes/{id}/file-history",
  "chart-reviews:drg-adds-held":
    "/chart-reviews/{chartId}/diagnosis-review-groups-adds/{drgId}/held",
  "chart-reviews:drg-deletes-held":
    "/chart-reviews/diagnosis-review-groups-deletes/{id}/held",
  "chart-reviews:drg-adds-dispositions":
    "/chart-reviews/{chartId}/diagnosis-review-groups/{drgId}/diagnosis-review-group-dispositions",
  "chart-reviews:drg-deletes-dispositions":
    "/chart-reviews/diagnosis-review-groups-deletes/{id}/diagnosis-review-group-deletes-dispositions",
  // matchpoint chooser
  "chart-reviews:drg-edit:matchpoint-chooser":
    "/chart-reviews/{chartId}/diagnosis-review-groups-adds/{drgId}/matchpoint-by-drg/{cmRunSid}",
  "chart-reviews:drg-edit:matchpoint-chooser:meta":
    "/chart-reviews/{chartId}/diagnosis-review-groups-adds/{drgId}/matchpoint-by-drg/summary",
  // claim matching params
  "claims-matching:param-settings":
    "/claims-matching-params/param-settings/{paramSetSid}",
  "claims-matching:param-settings:meta":
    "/claims-matching-params/param-settings/{paramSetSid}/summary",
  "claims-matching:param-type-settings":
    "/claims-matching-params/param-type-settings/{paramTypeSetSid}",
  "claims-matching:param-type-settings:meta":
    "/claims-matching-params/param-type-settings/{paramTypeSetSid}/summary",
  // adds bulk updates (by project)
  "chart-reviews:drg-adds:bulk-update:filtered":
    "/chart-reviews/projects/{projectId}/diagnosis-review-groups-adds-bulk-update/update-filtered",
  "chart-reviews:drg-adds:bulk-update:invalidate-filtered":
    "/chart-reviews/projects/{projectId}/diagnosis-review-groups-adds-bulk-update/invalidate-filtered",
  "chart-reviews:drg-adds:bulk-update:selected":
    "/chart-reviews/projects/{projectId}/diagnosis-review-groups-adds-bulk-update/update-selected",
  "chart-reviews:drg-adds:bulk-update:invalidate-selected":
    "/chart-reviews/projects/{projectId}/diagnosis-review-groups-adds-bulk-update/invalidate-selected",
  "chart-reviews:drg-adds:bulk-update:errors":
    "/chart-reviews/projects/{projectId}/diagnosis-review-groups-adds-bulk-update/errors/{workSid}",
  // deletes bulk updates
  "chart-reviews:drg-deletes:bulk-update:filtered":
    "/chart-reviews/diagnosis-review-groups-deletes-bulk-update/update-filtered",
  "chart-reviews:drg-deletes:bulk-update:selected":
    "/chart-reviews/diagnosis-review-groups-deletes-bulk-update/update-selected",
  "chart-reviews:drg-deletes:bulk-update:errors":
    "/chart-reviews/diagnosis-review-groups-deletes-bulk-update/errors/{workSid}",
  // approve pending chart deletes
  "chart-reviews:pending-deletes:bulk-update:filtered":
    "/chart-reviews/pending-chart-deletes-bulk-approval/create-filtered",
  "chart-reviews:pending-deletes:bulk-update:invalidate-filtered":
    "/chart-reviews/diagnosis-review-groups-deletes-bulk-update/invalidate-filtered",
  "chart-reviews:pending-deletes:bulk-update:selected":
    "/chart-reviews/pending-chart-deletes-bulk-approval/create-selected",
  "chart-reviews:pending-deletes:bulk-update:invalidate-selected":
    "/chart-reviews/diagnosis-review-groups-deletes-bulk-update/invalidate-selected",
  "chart-reviews:pending-deletes:bulk-update:errors":
    "/chart-reviews/pending-chart-deletes-bulk-approval/errors/{workSid}",
  // search APIs
  "chart-reviews:members": "/members/search",
  "chart-reviews:providers": "/providers/search",

  // LINKS
  "side-menu": "/accounts/self/links",

  // REPORTING PREFERENCES
  "reporting-preferences": "/accounts/self/reporting-preferences",

  // TOOLS

  // client user/roles report
  "tools.user-report": "/reports/client/user-report",
  "tools.hcc-disease-hierarchies": "/reports/hcc-disease-categories",
  // lookup
  "tools.diagnosis-codes": "/code/lookup/diag",
  "tools.procedure-codes": "/code/lookup/procedure",
  "tools.national-drug-codes": "/code/lookup/ndc",
  // parameter settings
  "tools.parameter-settings": "/claims-matching-params/param-settings/current",
  "tools.parameter-settings.summary":
    "/claims-matching-params/param-settings/current/summary",
  "tools.parameter-type-settings":
    "/claims-matching-params/param-type-settings/current",
  "tools.parameter-type-settings.summary":
    "/claims-matching-params/param-type-settings/current/summary",

  // ADMIN
  "admin.accounts": "/admin/accounts",
  "admin.accounts:roles": "/admin/accounts/roles",
  "admin.event-history": "/admin/event-history",
  "admin.config-settings": "/admin/config-settings",
};

export { config as default };
